import {
  Component,
  OnInit,
  input,
  output,
  inject,
  ChangeDetectionStrategy,
  effect,
  untracked,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { InputTypeIcons, InputTypes } from "../../../locale/consts.en";
import { ControlType } from "./item.model";
import {
  FORM_FIELD_COMMENT_TYPE_ICONS,
  FormFieldComment,
} from "../comment/comment.model";
import {
  CommentDialogComponent,
  CommentDialogData,
} from "../comment/comment-dialog/comment-dialog.component";
import { ProductCategory } from "../../master-data/master-data.model";
import {
  ExpressionColumnInfo,
  ExpressionErrorStateMatcher,
  expressionSyntaxValidator,
  validateExpression,
} from "../table-field/expression-validator";
import { TableFieldCondition } from "../table-field/table-field.model";
import {
  TableFieldConditionDialogComponent,
  TableFieldConditionDialogData,
} from "../table-field/table-filed-condition-dialog/table-field-condition-dialog.component";
import {
  DropdownEditValuesDialogComponent,
  DropdownEditValuesDialogData,
  DropdownValueItem,
} from "../table-field/dropdown-edit-values-dialog/dropdown-edit-values-dialog.component";
import {
  formulaVariables,
  isAttachmentOnly,
  numericCoverage,
  parseValueMap,
} from "./formula-variables";

const DESCRIPTION_TYPES: ControlType[] = [
  ControlType.TEXT,
  ControlType.INTEGER,
  ControlType.DECIMAL,
  ControlType.DROPDOWN,
  ControlType.DATETIME,
  ControlType.DATERANGE,
];

@Component({
  selector: "app-item",
  templateUrl: "./item.component.html",
  styleUrls: ["./item.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ItemComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);

  readonly itemForm = input<FormGroup>(undefined);
  readonly index = input<number>(undefined);
  /** The whole template form: its fields are the variables of formula fields. */
  readonly template = input<FormGroup | null>(null);
  /** All product categories: their fixed attributes are formula variables in attachment-only templates. */
  readonly productCategories = input<ProductCategory[]>([]);
  readonly deleteItem = output<number>();

  inputTypes = InputTypes;
  inputTypeIcons = InputTypeIcons;
  commentTypeIcons = FORM_FIELD_COMMENT_TYPE_ICONS;
  FieldTypes = ControlType;
  addGroup: FormGroup;

  /** The formula of an EXPRESSION field, kept in values[0]. */
  formulaControl = new FormControl<string>("");
  expressionErrorMatcher = new ExpressionErrorStateMatcher();

  constructor() {
    // Categories arrive asynchronously - re-check the formula once they do.
    effect(() => {
      this.productCategories();
      untracked(() => {
        if (this.itemForm()) {
          this.formulaControl.updateValueAndValidity();
          this.itemForm().updateValueAndValidity();
        }
      });
    });
  }

  ngOnInit() {
    this.addGroup = this.fb.group({
      addValue: [""],
    });
    if (this.isExpression()) {
      this.formulaControl.setValue(this.firstValue() ?? "");
    }
    this.formulaControl.setValidators([
      expressionSyntaxValidator(() => this.formulaVariables()),
    ]);
    this.formulaControl.updateValueAndValidity();
    this.itemForm().addValidators(this.formulaFieldValidator);
    this.itemForm().updateValueAndValidity();
  }

  getValues(form) {
    return form.get("values").controls;
  }

  delete() {
    this.deleteItem.emit(this.index());
  }

  get comment(): FormFieldComment | null {
    return this.itemForm().controls["comment"]?.value ?? null;
  }

  openCommentDialog(): void {
    const data: CommentDialogData = { comment: this.comment };
    const dialogRef = this.dialog.open(CommentDialogComponent, {
      width: "520px",
      maxWidth: "96vw",
      data,
      autoFocus: false,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: FormFieldComment | null | undefined) => {
        if (result === undefined) {
          return;
        }
        this.itemForm().controls["comment"].setValue(result);
      });
  }

  addNewValue() {
    const val = this.addGroup.controls.addValue.value;
    if (val !== null) {
      const array = this.itemForm().controls.values as FormArray;
      array.push(new FormControl(val));
      this.addGroup.controls.addValue.setValue(null);
    }
  }

  deleteValue(index: number) {
    const array = this.itemForm().controls.values as FormArray;
    const removed = array.at(index)?.value as string;
    array.removeAt(index);
    // the option's number goes with it
    const valueMap = parseValueMap(this.itemForm().controls.valueMap?.value);
    if (removed in valueMap) {
      delete valueMap[removed];
      this.setValueMap(valueMap);
    }
  }

  typeSelected(e: ControlType) {
    const array = this.itemForm().controls.values as FormArray;
    switch (+e as ControlType) {
      case ControlType.INTEGER:
      case ControlType.DECIMAL:
      case ControlType.TEXT:
      case ControlType.BOOLEAN:
      case ControlType.DEVICES:
      case ControlType.DATE:
      case ControlType.DATETIME:
      case ControlType.DATERANGE:
        array.clear();
        array.setValidators(null);
        this.itemForm().controls.valueMap?.setValue(null);
        break;
      case ControlType.EXPRESSION:
      case ControlType.CONDITION:
        array.clear();
        array.setValidators(Validators.required);
        this.itemForm().controls.valueMap?.setValue(null);
        this.formulaControl.setValue("");
        break;
      case ControlType.MULTI:
        array.setValidators(Validators.required);
        this.itemForm().controls.valueMap?.setValue(null);
        break;
      default:
        array.setValidators(Validators.required);
    }
    array.updateValueAndValidity();
    if (!this.hasDescription()) {
      this.itemForm().controls.description.setValue(null);
    }
  }

  /** the mobile app shows the description as a placeholder only for these types */
  hasDescription(): boolean {
    return DESCRIPTION_TYPES.includes(+this.itemForm()?.controls.type.value);
  }

  // ---- formula fields ---------------------------------------------------

  isExpression(): boolean {
    return +this.itemForm()?.controls.type.value === ControlType.EXPRESSION;
  }

  isCondition(): boolean {
    return +this.itemForm()?.controls.type.value === ControlType.CONDITION;
  }

  isFormula(): boolean {
    return this.isExpression() || this.isCondition();
  }

  /** RADIO and DROPDOWN options can have numbers, for formulas. */
  hasNumericValues(): boolean {
    const type = +this.itemForm()?.controls.type.value;
    return type === ControlType.RADIO || type === ControlType.DROPDOWN;
  }

  formulaVariables(): ExpressionColumnInfo[] {
    return formulaVariables(
      this.template(),
      this.productCategories(),
      this.itemForm()?.controls.renderTag.value,
    );
  }

  /** Device attributes can be formula variables only where the device is known. */
  attributesAvailable(): boolean {
    return isAttachmentOnly(this.template());
  }

  formulaWarnings(): string[] {
    return validateExpression(this.formulaControl.value, this.formulaVariables())
      .warnings;
  }

  onFormulaChange(value: string) {
    const array = this.itemForm().controls.values as FormArray;
    array.clear();
    if (value !== "") {
      array.push(new FormControl(value));
    }
  }

  /**
   * Mirrors formulaControl's validator so an invalid formula also makes the
   * field (and so the template) invalid.
   */
  private formulaFieldValidator = (
    form: AbstractControl,
  ): ValidationErrors | null => {
    if (+form.get("type")?.value !== ControlType.EXPRESSION) {
      return null;
    }
    const values = form.get("values") as FormArray;
    const expression = values?.length > 0 ? (values.at(0).value as string) : "";
    const { errors } = validateExpression(expression, this.formulaVariables());
    return errors.length > 0 ? { expressionSyntax: errors } : null;
  };

  conditionSummary(): string | null {
    const condition = this.condition();
    return condition
      ? `${condition.expression} → ${condition.resultTrue} / ${condition.resultFalse}`
      : null;
  }

  openConditionDialog() {
    const data: TableFieldConditionDialogData = {
      condition: this.condition(),
      columns: this.formulaVariables(),
    };
    const dialogRef = this.dialog.open(TableFieldConditionDialogComponent, {
      width: "560px",
      maxWidth: "95vw",
      data,
    });
    dialogRef.afterClosed().subscribe((result: TableFieldCondition) => {
      if (result != null) {
        const array = this.itemForm().controls.values as FormArray;
        array.clear();
        array.push(new FormControl(encodeBase64Json(result)));
      }
    });
  }

  private condition(): TableFieldCondition | null {
    const encoded = this.firstValue();
    if (!this.isCondition() || !encoded) {
      return null;
    }
    try {
      return decodeBase64Json(encoded) as TableFieldCondition;
    } catch {
      return null;
    }
  }

  private firstValue(): string | null {
    const array = this.itemForm()?.controls.values as FormArray;
    return array?.length > 0 ? (array.at(0).value as string) : null;
  }

  // ---- numbers of RADIO/DROPDOWN options --------------------------------

  numericValuesSummary(): { mapped: number; total: number } {
    const values = (this.itemForm().controls.values as FormArray).controls
      .map((c) => c.value as string)
      .filter((v) => !!v);
    const valueMap = parseValueMap(this.itemForm().controls.valueMap?.value);
    return {
      mapped: values.filter((v) => valueMap[v] != null).length,
      total: values.length,
    };
  }

  numericValuesIncomplete(): boolean {
    const coverage = numericCoverage(this.itemForm());
    return coverage.hasAny && !coverage.allMapped;
  }

  editNumericValues(): void {
    const valuesArray = this.itemForm().controls.values as FormArray;
    const valueMap = parseValueMap(this.itemForm().controls.valueMap?.value);
    const data: DropdownEditValuesDialogData = {
      values: valuesArray.controls.map((control) => {
        const value = (control.value as string) ?? "";
        return { value, numericValue: valueMap[value] ?? null };
      }),
      numericValueDisabled: false,
    };
    const dialogRef = this.dialog.open<
      DropdownEditValuesDialogComponent,
      DropdownEditValuesDialogData,
      DropdownValueItem[] | undefined
    >(DropdownEditValuesDialogComponent, {
      width: "680px",
      maxWidth: "95vw",
      data,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      valuesArray.clear();
      const nextValueMap: Record<string, number | null> = {};
      result.forEach((item) => {
        valuesArray.push(new FormControl(item.value));
        nextValueMap[item.value] = item.numericValue;
      });
      this.setValueMap(nextValueMap);
    });
  }

  // only numbers are kept; no numbers -> no map
  private setValueMap(valueMap: Record<string, number | null>) {
    const numbers = Object.fromEntries(
      Object.entries(valueMap).filter(
        ([, value]) => value != null && !isNaN(+value),
      ),
    );
    this.itemForm().controls.valueMap?.setValue(
      Object.keys(numbers).length > 0 ? JSON.stringify(numbers) : null,
    );
  }
}

// UTF-8 base64 JSON, as the table condition columns store it
function encodeBase64Json(value: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

function decodeBase64Json(encoded: string): unknown {
  const binary = atob(encoded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}
